import React from "react";
import { Button, Dialog, KeyValueList, QueryResult } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { AllowedTo } from "Components";
import { PERMISSION } from "Constants";
import { useGetSharedReportLinkRecipientsQuery } from "../../../services/sharedReports.api";
import { SharedReportInfo } from "../../../types";
import { isSharedReportLinkExpired } from "../../../utils";
import getReportSettingsList from "../common/ReportSettings/reportSettingsList";
import RecipientsTable from "../common/WizardSteps/RecipientsTable/RecipientsTable";
import styles from "./linkOverviewDialog.module.scss";

type LinkOverviewDialogProps = {
  sharedReport: SharedReportInfo;
  purl: string;
  onClose: () => void;
  isOpen: boolean;
  openResendLinkDialog?: () => void;
};

const LinkOverviewDialogContent = ({
  sharedReport,
  purl,
  onClose,
  openResendLinkDialog,
}: Omit<LinkOverviewDialogProps, "isOpen">): React.JSX.Element => {
  const {
    data: existingRecipients,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetSharedReportLinkRecipientsQuery({ token_key: sharedReport.token });

  const existingRecipientsQueryStatus = getQueryStatus({
    isLoading,
    isFetching,
    isError,
    error,
    data: existingRecipients,
  });

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>
          <em>{sharedReport.settings.label}</em> Overview
        </Dialog.Header.Title>
        <Dialog.Header.Subtitle>
          <code>{purl}</code>
        </Dialog.Header.Subtitle>
      </Dialog.Header>
      <Dialog.Body>
        <Dialog.Section>
          <Dialog.Section.Title>
            Recipients ({existingRecipients?.length ?? 0})
          </Dialog.Section.Title>
          <Dialog.Section.Content>
            <QueryResult status={existingRecipientsQueryStatus}>
              {existingRecipients && (
                <RecipientsTable
                  recipients={existingRecipients}
                  tableContainerClassName={styles["table-container"]}
                />
              )}
            </QueryResult>
          </Dialog.Section.Content>
        </Dialog.Section>
        <KeyValueList
          list={[
            "Settings overview",
            getReportSettingsList({
              expirationDate: sharedReport.settings.expiration_date,
              sharedReportOptions: sharedReport.settings,
              sharedBy: sharedReport.user,
              sharedDate: sharedReport.created,
            }),
          ]}
        />
      </Dialog.Body>
      <Dialog.Footer>
        <Dialog.Footer.Actions>
          <Button color="secondary" onClick={onClose}>
            Cancel
          </Button>
          {openResendLinkDialog && !isSharedReportLinkExpired(sharedReport) && (
            <AllowedTo permissions={[PERMISSION.SHARE_REPORT]}>
              <Button color="primary" onClick={openResendLinkDialog}>
                Send link
              </Button>
            </AllowedTo>
          )}
        </Dialog.Footer.Actions>
      </Dialog.Footer>
    </>
  );
};

const LinkOverviewDialog = ({
  isOpen,
  ...props
}: LinkOverviewDialogProps): React.JSX.Element => (
  <Dialog.Frame size="md" isOpen={isOpen} onClose={props.onClose}>
    <LinkOverviewDialogContent {...props} />
  </Dialog.Frame>
);

export default LinkOverviewDialog;
